import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, TextField, Checkbox, ListItemText, MenuItem,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import {
  reportsService,
  ProductivityDetailItem,
  ReportProfessionalItem,
} from '../services/reportsService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { filterFieldSx } from '../theme/filterStyles';

/**
 * Impressão da Produtividade (RN-215e): **um profissional por folha**, com os atendimentos que
 * ele realizou no período — data, horário, paciente, tipo de serviço e se a evolução foi feita.
 *
 * A tela chega com o recorte do relatório e permite apertá-lo antes de imprimir, sem voltar:
 * competência, as duas datas e a seleção de profissionais. Mesmo comportamento da impressão da
 * agenda (RN-120), de onde vem a estrutura de moldura por profissional.
 *
 * Cabeçalho e rodapé saem do TEMPLATE PADRÃO da entidade e se repetem em toda página por
 * thead/tfoot (RN-534e) — a folha de cada profissional é entregue a ele.
 */
const formatarData = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split('-').map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` };
};

/** Últimos 12 meses — a competência de um relatório é sempre passada ou corrente. */
const COMPETENCIAS = (() => {
  const hoje = new Date();
  const lista: { value: string; label: string }[] = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    lista.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    });
  }
  return lista;
})();

const ProductivityPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();

  const bounds = competenceBounds(currentCompetence());
  const [competence, setCompetence] = useState(getParam('competence') || '');
  const [fromDate, setFromDate] = useState(getParam('from') || bounds.from);
  const [toDate, setToDate] = useState(getParam('to') || bounds.to);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    (getParam('professionals') || '').split(',').filter(Boolean),
  );

  const [professionals, setProfessionals] = useState<ReportProfessionalItem[]>([]);
  const [items, setItems] = useState<ProductivityDetailItem[]>([]);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'productivity');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setErro('');
    try {
      setItems(await reportsService.getProductivityDetails({
        from: fromDate,
        to: toDate,
        professionalIds: selectedIds,
      }));
    } catch {
      setErro('Não foi possível carregar os atendimentos do período.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedIds]);

  useEffect(() => {
    reportsService.getProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
    // Template PADRÃO da entidade; sem nenhum cadastrado a folha sai sem cabeçalho, e não quebra.
    if (user?.entityId) {
      printTemplateService
        .getByEntity(user.entityId)
        .then((lista) => setTemplates(lista))
        .catch(() => setTemplates([]));
    }
  }, [user?.entityId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const escolherCompetencia = (valor: string) => {
    setCompetence(valor);
    if (valor) {
      const b = competenceBounds(valor);
      setFromDate(b.from);
      setToDate(b.to);
    }
  };

  const alternarTodos = () =>
    setSelectedIds((atual) => (atual.length === professionals.length ? [] : professionals.map((p) => p.externalId)));

  /** Um bloco por profissional, na ordem em que a API já devolveu (nome, depois horário). */
  const blocos = useMemo(() => {
    const mapa = new Map<string, { nome: string; itens: ProductivityDetailItem[] }>();
    items.forEach((i) => {
      const chave = i.professionalExternalId || i.professionalName;
      if (!mapa.has(chave)) mapa.set(chave, { nome: i.professionalName, itens: [] });
      mapa.get(chave)!.itens.push(i);
    });
    return Array.from(mapa.values());
  }, [items]);

  const cabecalhoClinica = () => template && (
    <div className="print-frame-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6mm' }}>
        <div style={{ flexShrink: 0 }}>
          {template?.file ? (
            <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
          ) : null}
        </div>
        <div
          style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }}
        />
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
    </div>
  );

  const rodapeClinica = () => template && (
    <div
      style={{ textAlign: 'center', fontSize: '9pt', paddingTop: '6mm' }}
      dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }}
    />
  );

  const emitidoEm = () => (
    <Typography sx={{ fontSize: '9pt', color: '#6c757d' }}>
      Emitido em {new Date().toLocaleDateString('pt-BR')}
    </Typography>
  );

  const celulaCabecalho: React.CSSProperties = {
    padding: '2mm 3mm', border: '1px solid #ccc', background: '#f1f3f5',
    fontWeight: 600, textAlign: 'left',
  };
  const celula: React.CSSProperties = {
    padding: '2mm 3mm', border: '1px solid #ccc', overflowWrap: 'anywhere',
  };

  const secaoProfissional = (bloco: { nome: string; itens: ProductivityDetailItem[] }) => {
    const evoluidos = bloco.itens.filter((i) => i.evolved).length;
    return (
      <>
        <Box sx={{ mb: '4mm' }}>
          <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
            Atendimentos realizados
          </Typography>
          <Typography sx={{ fontSize: '11pt', color: '#212529', fontWeight: 600 }}>
            {bloco.nome}
          </Typography>
          <Typography sx={{ fontSize: '10pt', color: '#495057' }}>
            {formatarData(fromDate)} a {formatarData(toDate)}
            {' · '}
            {bloco.itens.length} atendimento{bloco.itens.length === 1 ? '' : 's'}
            {' · '}
            {evoluidos} com evolução ({bloco.itens.length > 0 ? Math.round((100 * evoluidos) / bloco.itens.length) : 0}%)
          </Typography>
        </Box>

        <table style={{
          width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse',
          fontSize: '10pt', marginBottom: '4mm',
        }}>
          <thead>
            <tr>
              <th style={{ ...celulaCabecalho, width: '24mm' }}>Data</th>
              <th style={{ ...celulaCabecalho, width: '18mm' }}>Horário</th>
              <th style={celulaCabecalho}>Paciente</th>
              <th style={celulaCabecalho}>Serviço</th>
              <th style={{ ...celulaCabecalho, width: '22mm' }}>Evolução</th>
            </tr>
          </thead>
          <tbody>
            {bloco.itens.map((item, idx) => (
              <tr key={`${item.professionalExternalId}-${item.date}-${item.startTime}-${idx}`}>
                <td style={celula}>{formatarData(item.date)}</td>
                <td style={celula}>{item.startTime}</td>
                <td style={celula}>{item.patientName}</td>
                <td style={celula}>{item.serviceName}</td>
                <td style={{ ...celula, textAlign: 'center' }}>{item.evolved ? 'Sim' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: { xs: '0.75rem', sm: '2rem' },
      '@media print': { padding: 0, backgroundColor: '#fff' },
    }}>
      <style>{`
        .print-frame { width: 100%; border-collapse: collapse; }
        .print-frame + .print-frame { margin-top: 10mm; }
        .print-frame > thead { display: table-header-group; }
        .print-frame > tfoot { display: table-footer-group; }

        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .print-frame { min-height: 262mm; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td { vertical-align: top; padding: 0; }
          .print-frame > tfoot > tr > td { vertical-align: bottom; padding: 0; }
          .print-frame-header img { max-height: 18mm !important; }
          /* Cada profissional comeca em folha nova, menos o primeiro. A regra vale na TABELA:
             quebra forcada dentro de <td> e inconsistente entre navegadores. */
          .print-frame + .print-frame {
            break-before: page;
            page-break-before: always;
            margin-top: 0;
          }
          tr { break-inside: avoid; }
        }
      `}</style>

      <Box className="no-print" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          select size="small" label="Competência" value={competence}
          onChange={(e) => escolherCompetencia(e.target.value)}
          sx={{ ...filterFieldSx, minWidth: 170 }} InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Por período</MenuItem>
          {COMPETENCIAS.map((c) => (
            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" type="date" label="De" value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setCompetence(''); }}
          sx={{ ...filterFieldSx, minWidth: 150 }} InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" type="date" label="Até" value={toDate}
          onChange={(e) => { setToDate(e.target.value); setCompetence(''); }}
          sx={{ ...filterFieldSx, minWidth: 150 }} InputLabelProps={{ shrink: true }}
        />
        <TextField
          select size="small" label="Profissionais" value={selectedIds}
          onChange={(e) => {
            const v = e.target.value;
            setSelectedIds((typeof v === 'string' ? v.split(',') : v).filter((x) => x !== 'all'));
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selected) => {
              const ids = selected as string[];
              if (ids.length === 0) return 'Todos';
              if (ids.length > 2) return `${ids.length} selecionados`;
              return professionals.filter((p) => ids.includes(p.externalId)).map((p) => p.name).join(', ');
            },
          }}
          sx={{ ...filterFieldSx, minWidth: 220 }} InputLabelProps={{ shrink: true }}
        >
          <MenuItem
            value="all"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); alternarTodos(); }}
            sx={{ backgroundColor: '#f8f9fa !important', fontWeight: 600, borderBottom: '1px solid #e9ecef' }}
          >
            <Checkbox
              checked={professionals.length > 0 && selectedIds.length === professionals.length}
              indeterminate={selectedIds.length > 0 && selectedIds.length < professionals.length}
              sx={{ padding: '4px 8px 4px 4px' }}
            />
            <ListItemText primary={`Selecionar Todos (${professionals.length})`} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
          </MenuItem>
          {professionals.map((p) => (
            <MenuItem key={p.externalId} value={p.externalId}>
              <Checkbox size="small" checked={selectedIds.includes(p.externalId)} />
              <ListItemText primary={p.name} primaryTypographyProps={{ fontSize: 14 }} />
            </MenuItem>
          ))}
        </TextField>
        <PrintTemplateBar choice={templateChoice} sx={{ gridColumn: '1 / -1' }} />
        <Button
          variant="contained" startIcon={<Print />} onClick={() => window.print()}
          disabled={loading || blocos.length === 0}
        >
          Imprimir
        </Button>
      </Box>

      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : erro ? (
          <Typography sx={{ p: 3, fontSize: 14, color: '#dc3545' }}>{erro}</Typography>
        ) : blocos.length === 0 ? (
          <Typography className="no-print" sx={{ p: 3, fontSize: 14, color: '#6c757d' }}>
            Nenhum atendimento realizado no período com os filtros aplicados. A impressão considera
            apenas sessões com presença "Compareceu" — as mesmas do relatório.
          </Typography>
        ) : (
          blocos.map((bloco) => (
            /* Uma MOLDURA COMPLETA por profissional: a folha dele leva o cabeçalho da clínica em
               todas as páginas, e não só na primeira do documento. */
            <table className="print-frame" key={bloco.nome}>
              <thead><tr><td>{cabecalhoClinica()}</td></tr></thead>
              <tfoot><tr><td>{rodapeClinica()}</td></tr></tfoot>
              <tbody><tr><td>
                {secaoProfissional(bloco)}
                {emitidoEm()}
              </td></tr></tbody>
            </table>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ProductivityPrint;
